"use client";

import { motion } from "framer-motion";
import { useEffect } from "react";

const safariStyles = `
  .gradient-text-white {
    background: linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    background: -webkit-linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    background: -moz-linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  @supports not (-webkit-background-clip: text) {
    .gradient-text-white {
      background: none;
      color: #ffffff;
    }
  }
`;

const clientLogos = [
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/5f223281fca4c0b465e2c564e62e09b5.png",
    alt: "Client 1"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/06d33c174aa0979dc98709afed24fc97.png",
    alt: "Client 2"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/d69572c2723a92ba764f456760bf7ab2.jpg",
    alt: "Client 3"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/a52bc4fcae181ffcca84214c1449a713.png",
    alt: "Client 4"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/e7c6879370198b8bb2e1f86df8425d20.svg",
    alt: "Client 5"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/4e2044e815e370f8ee64fc3a30e9e959.png",
    alt: "Client 6"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/7ad6ba5178884fb6d4bad2a5441f88ca.jpg",
    alt: "Client 7"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/390721eaaf028acd09e82226bef7f69e.png",
    alt: "Client 8"
  },
  {
    url: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/278e19b3e122b7c397ccb0b122211b51.jpg",
    alt: "Client 9"
  },
  {
    url: "https://shg.ge/wp-content/uploads/2024/03/2-2.png",
    alt: "Smart House Georgia"
  }
];

export default function OurClients() {
  useEffect(() => {
    // Inject Safari gradient styles
    const styleElement = document.createElement('style');
    styleElement.textContent = safariStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <section
      id="our-clients"
      className="relative py-24 md:py-36 overflow-hidden"
    >
      {/* Subtle overlay for content readability */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        {/* Light overlay to ensure text readability without blocking the global gradient */}
        <div className="absolute inset-0 section-overlay" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text-white">
            Our Clients
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Trusted by leading brands worldwide.
          </p>
        </motion.div>

        {/* Clients Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 max-w-6xl mx-auto">
          {clientLogos.map((client, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                ease: "easeOut" 
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              className="group relative"
            >
              {/* Client Logo Card */}
              <div className="relative aspect-square p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm hover:border-accent/30 transition-all duration-500 cursor-pointer">
                {/* Glassmorphism effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Logo Container */}
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="relative w-full h-full">
                    <img
                      src={client.url}
                      alt={client.alt}
                      className="absolute inset-0 w-full h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 opacity-70 group-hover:opacity-100"
                      loading="lazy"
                    />
                  </div>
                </div>
                
                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-accent2/10 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Floating Animation for some logos */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-accent/20 rounded-full"
              style={{
                left: `${20 + i * 30}%`,
                top: `${30 + i * 20}%`,
              }}
              animate={{
                y: [-10, 10, -10],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 3 + i,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.5,
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}